import React from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
  CategoryScale,
  SubTitle,
  Title,
} from "chart.js";

ChartJS.register(
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
  CategoryScale,
  SubTitle,
  Title,
);

function ScreensChart({ dataPoints = [], updateVideoTrack }) {
  if (!dataPoints || dataPoints.length === 0)
    return (
      <div className="text-center text-gray-500 text-base pt-4">
        No screen activity data found.
      </div>
    );

  const values = dataPoints.map((point) => {
    const suspiciousFound = point.totalScreensRunning > 1;

    return {
      x: point.elapsedTime,
      y: point.totalScreensRunning,
      screens: point.screenNames,
      suspiciousFound,
    };
  });

  const maxX = Math.max(...dataPoints.map((p) => p.elapsedTime));
  const maxY = Math.max(...dataPoints.map((p) => p.totalScreensRunning));

  const convertSecondsToTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
  };

  const data = {
    datasets: [
      {
        label: "Screens Running",
        data: values,
        borderColor: "rgb(139, 92, 246)",
        backgroundColor: "rgba(139, 92, 246, 0.15)",
        borderWidth: 2,
        fill: true,
        tension: 0,

        pointRadius: 5,

        pointBackgroundColor: (context) => {
          const raw = context.raw;
          return raw.suspiciousFound ? "#ef4444" : "rgb(139, 92, 246)";
        },

        pointBorderColor: "#fff",
        pointBorderWidth: 2,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,

    scales: {
      x: {
        type: "linear",
        min: 0,
        max: maxX + 10,
        title: {
          display: true,
          text: "Elapsed Time (mm:ss)",
          font: { weight: "bold" },
        },
        ticks: {
          callback: (value) => convertSecondsToTime(value),
        },
      },

      y: {
        min: 0,
        max: maxY + 1,
        title: {
          display: true,
          text: "Running Screens",
          font: { weight: "bold" },
        },
        ticks: {
          stepSize: 1,
        },
      },
    },

    plugins: {
      title: {
        display: true,
        text: "Display Screens Activity Timeline",
        font: {
          size: 18,
          weight: "bold",
        },
        padding: {
          top: 5,
          bottom: 7,
        },
      },

      subtitle: {
        display: true,
        text: "(Number of screens detected throughout the interview)",
        font: {
          size: 13.6,
        },
        color: "#6b7280",
        padding: {
          bottom: 12,
        },
      },

      legend: {
        display: true,
        position: "top",

        onClick: (e, legendItem, legend) => {
          if (legendItem.text === "Multiple Screens Detected") return;

          const index = legendItem.datasetIndex;
          const ci = legend.chart;

          if (ci.isDatasetVisible(index)) {
            ci.hide(index);
            legendItem.hidden = true;
          } else {
            ci.show(index);
            legendItem.hidden = false;
          }
        },

        labels: {
          generateLabels: (chart) => {
            const original =
              ChartJS.defaults.plugins.legend.labels.generateLabels(chart);

            return [
              ...original,
              {
                text: "Multiple Screens Detected",
                fillStyle: "#ef4444",
                strokeStyle: "#ef4444",
                hidden: false,
              },
            ];
          },
        },
      },

      tooltip: {
        displayColors: false,
        backgroundColor: "#111827",
        titleColor: "#f9fafb",
        bodyColor: "#e5e7eb",
        padding: 14,

        callbacks: {
          title: (context) =>
            `Elapsed: ${convertSecondsToTime(context[0].parsed.x)}`,

          label: (context) => `Screens Running: ${context.parsed.y}`,

          afterBody: (context) => {
            const screens = context[0].raw.screens || [];

            if (!screens.length) return ["No screens detected"];

            return screens.map((screen) => {
              const label = `${screen.name} (${screen.resolution})`;

              return screen.primary ? `🟢 ${label} (Primary)` : `🔴 ${label}`;
            });
          },
        },
      },
    },

    interaction: {
      mode: "nearest",
      intersect: true,
    },

    onClick: (event, elements) => {
      if (elements.length > 0 && updateVideoTrack) {
        const clickedX = elements[0].element.$context.parsed.x;

        updateVideoTrack(clickedX);

        window.scrollTo({
          top: 200,
          behavior: "smooth",
        });
      }
    },

    onHover: (event, elements) => {
      event.native.target.style.cursor =
        elements.length > 0 ? "pointer" : "default";
    },
  };

  return (
    <div>
      <div className="h-[320px]">
        <Line data={data} options={options} />
      </div>
      <div className="mt-5 max-w-[88rem] mx-auto bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900">
        <p className="mb-[.35rem]">
          <span className="font-semibold">How to read:</span> This graph
          displays the number of display screens detected at each timestamp
          during the interview. Hover over any point to view the screens active
          at that moment. Instances where more than one screen is detected are
          highlighted in <span className="text-red-500 font-medium">red</span>.
        </p>

        <p className="text-blue-800">
          <span className="font-semibold">Note:</span> A higher number of red
          markers or multiple screen detections may indicate lower candidate
          integrity and the potential use of additional screens to cheat during the
          interview.
        </p>
      </div>
    </div>
  );
}

export default ScreensChart;
